import {Component, OnInit} from '@angular/core';
import {Fluid} from 'primeng/fluid';
import {FormsModule} from '@angular/forms';
import {MultiSelect, MultiSelectChangeEvent} from 'primeng/multiselect';
import {FilterMetadata, PrimeTemplate} from 'primeng/api';
import {TableColumnReorderEvent, TableLazyLoadEvent, TableModule} from 'primeng/table';
import {StorageService} from '../../services/app/storage.service';
import {DateRangeFilterValue, PurchaseOrderItemService} from '../../purchase-order-item/purchase-order-item.service';
import {PurchaseOrderItem} from '../../purchase-order-item/purchase-order-item';
import {CurrencyPipe, DatePipe} from '@angular/common';
import {PurchaseOrderStatusPipe} from '../../purchase-order/purchase-order-status-pipe';
import {PurchaseOrderStatus} from '../../purchase-order/purchase-order-status.enum';
import {PURCHASE_ORDER_STATUS_LABELS} from '../../purchase-order/purchase-order-status.constants';
import {ManufacturerService} from '../../manufacturer/manufacturer.service';
import {Manufacturer} from '../../manufacturer/manufacturer';

const PURCHASE_ORDER_ITEMS_LAST_PAGE_SIZE = 'purchase_order_items_last_page_size';
const PURCHASE_ORDER_ITEMS_LAST_SELECTED_COLUMNS = 'purchase_order_items_last_selected_columns';
const PURCHASE_ORDER_ITEMS_LAST_FILTERS = 'purchase_order_items_last_filters';

type ColumnType = 'string' | 'currency' | 'date' | 'number';
type FilterValue = string | number | Date | DateRangeFilterValue | string[];
type PipeName = 'PurchaseOrderStatusPipe';

interface Column {
    field: keyof PurchaseOrderItem & string;
    header: string;
    type: ColumnType;
    sortable: boolean;
    pipe?: PipeName;
}

interface SelectOption {
    label: string;
    value: string;
}

@Component({
    selector: 'app-purchase-orders',
    imports: [
        Fluid,
        FormsModule,
        MultiSelect,
        PrimeTemplate,
        TableModule,
        DatePipe,
        CurrencyPipe,
    ],
    providers: [PurchaseOrderStatusPipe],
    templateUrl: './purchase-orders.html',
    styleUrl: './purchase-orders.scss',
})
export class PurchaseOrders implements OnInit {

    protected purchaseOrderItems!: PurchaseOrderItem[];
    protected itemsPerPage: number = 10;
    protected loading: boolean = false;
    protected totalNbPurchaseOrderItems!: number;

    protected cols: Column[] = [];
    protected selectedColumns: Column[] = [];
    protected tableFilters: Record<string, FilterMetadata> = {};

    protected readonly maxSelectedColumnLabels = 3;

    protected readonly purchaseOrderStatusOptions: SelectOption[] = Object.values(PurchaseOrderStatus).map(status => ({
        label: PURCHASE_ORDER_STATUS_LABELS[status],
        value: status,
    }));

    protected manufacturers: Manufacturer[] = [];

    constructor(
        protected storage: StorageService,
        protected purchaseOrderItemService: PurchaseOrderItemService,
        protected purchaseOrderStatusPipe: PurchaseOrderStatusPipe,
        protected manufacturerService: ManufacturerService,
    ) {
    }

    ngOnInit() {
        const storedItemsPerPage = this.storage.get(PURCHASE_ORDER_ITEMS_LAST_PAGE_SIZE);
        if (typeof storedItemsPerPage === 'number') {
            this.itemsPerPage = storedItemsPerPage;
        }

        this.cols = [
            { field: 'poId', header: 'PO ID', type: 'string', sortable: false },
            { field: 'status', header: 'Status', type: 'string', sortable: true, pipe: 'PurchaseOrderStatusPipe' },
            { field: 'productId', header: 'Product ID', type: 'string', sortable: false },
            { field: 'productDisplayName', header: 'Product', type: 'string', sortable: false },
            { field: 'manufacturer', header: 'Manufacturer', type: 'string', sortable: true },
            { field: 'mpn', header: 'MPN', type: 'string', sortable: false },
            { field: 'quantity', header: 'Quantity', type: 'number', sortable: true },
            { field: 'received', header: 'Received', type: 'number', sortable: true },
            { field: 'billed', header: 'Billed', type: 'number', sortable: true },
            { field: 'expectedReceiptDate', header: 'Expected Receipt Date', type: 'date', sortable: true },
            { field: 'etaPort', header: 'ETA Port', type: 'date', sortable: true },
            { field: 'masterBillOfLading', header: 'Master Bill of Lading', type: 'string', sortable: false },
            { field: 'containerId', header: 'Container ID', type: 'string', sortable: false },
            { field: 'consignmentLocationName', header: 'Consignment Location', type: 'string', sortable: true },
            { field: 'inConsignmentDate', header: 'In Consignment Date', type: 'date', sortable: true },
            { field: 'legalEntity', header: 'Legal Entity', type: 'string', sortable: true },
            { field: 'euroPerWatt', header: 'Euro/Watt', type: 'currency', sortable: true },
            { field: 'rate', header: 'Rate', type: 'currency', sortable: true },
            { field: 'amount', header: 'Amount', type: 'currency', sortable: true },
            { field: 'linkedSalesOrder', header: 'Linked Sales Order', type: 'string', sortable: false },
            { field: 'purchaseOrderSubStatus', header: 'PO Sub-Status', type: 'string', sortable: true },
            { field: 'containerQuantity', header: 'Container Quantity', type: 'number', sortable: true },
            { field: 'palletQuantity', header: 'Pallet Quantity', type: 'number', sortable: true },
        ];

        const selectedColumns: Column[] | null = this.storage.get(PURCHASE_ORDER_ITEMS_LAST_SELECTED_COLUMNS);

        if (selectedColumns?.length) {
            this.selectedColumns = selectedColumns
                .map(selectedColumn => this.cols.find(column => column.field === selectedColumn.field))
                .filter((column): column is Column => column !== undefined);
        }

        if (!this.selectedColumns.length) {
            this.selectedColumns = this.cols.filter(column =>
                [
                    'poId',
                    'productId',
                    'productDisplayName',
                    'quantity',
                    'containerId',
                    'masterBillOfLading',
                ].includes(column.field),
            );
        }

        const storedFilters: Record<string, FilterValue> | null = this.storage.get(PURCHASE_ORDER_ITEMS_LAST_FILTERS);
        if (storedFilters) {
            this.tableFilters = this.createTableFiltersFromStoredFilters(storedFilters);
        }

        this.manufacturerService.getAll().subscribe(manufacturerCollection => {
            this.manufacturers = manufacturerCollection.getManufacturers();
        });
    }

    protected get selectedColumnDisplayMode(): 'chip' | 'comma' {
        return this.selectedColumns.length > this.maxSelectedColumnLabels ? 'comma' : 'chip';
    }

    protected tableUpdated(event: TableLazyLoadEvent): void {
        if (typeof event.rows === 'number' && typeof event.first === 'number') {
            let sortField = null;
            let sortOrder: 'asc' | 'desc' | null = null;

            if (event.sortField !== undefined && typeof event.sortField === 'string') {
                sortField = event.sortField;
                sortOrder = event.sortOrder === 1 ? 'asc' : 'desc';
            }

            this.itemsPerPage = event.rows;

            const filters = this.extractFilters(event.filters);
            this.saveFilters(filters);

            this.updatePurchaseOrderItemList(
                event.first / event.rows + 1,
                sortField,
                sortOrder,
                filters,
            );
        } else {
            console.log('Invalid TableLazyLoad event.');
        }
    }

    private extractFilters(filters: TableLazyLoadEvent['filters']): Record<string, FilterValue> {
        const result: Record<string, FilterValue> = {};

        if (!filters) {
            return result;
        }

        const filterMap = filters as Record<string, FilterMetadata | FilterMetadata[]>;

        for (const field of Object.keys(filterMap)) {
            const column = this.cols.find(col => col.field === field);
            const meta = filterMap[field];

            if (column?.type === 'date') {
                const dateRange = this.extractDateRangeFilter(meta);

                if (dateRange.from !== undefined || dateRange.to !== undefined) {
                    result[field] = dateRange;
                }

                continue;
            }

            const constraint = Array.isArray(meta) ? meta[0] : meta;

            if (constraint?.value === null || constraint?.value === undefined || constraint.value === '') {
                continue;
            }

            if (Array.isArray(constraint.value)) {
                if (constraint.value.length > 0) {
                    result[field] = constraint.value;
                }

                continue;
            }

            result[field] = constraint.value;
        }

        return result;
    }

    private extractDateRangeFilter(meta: FilterMetadata | FilterMetadata[] | undefined): DateRangeFilterValue {
        const constraints = Array.isArray(meta) ? meta : [meta];
        const result: DateRangeFilterValue = {};

        for (const constraint of constraints) {
            if (constraint?.value === null || constraint?.value === undefined || constraint.value === '') {
                continue;
            }

            if (this.isDateRangeFilterValue(constraint.value)) {
                if (constraint.value.from !== undefined && constraint.value.from !== '') {
                    result.from = constraint.value.from;
                }

                if (constraint.value.to !== undefined && constraint.value.to !== '') {
                    result.to = constraint.value.to;
                }

                continue;
            }

            const value = constraint.value instanceof Date
                ? constraint.value
                : new Date(constraint.value);

            const dateOnly = this.formatDateOnly(value);
            const matchMode = constraint.matchMode ?? 'dateIs';

            if (matchMode === 'dateAfter' || matchMode === 'after' || matchMode === 'gt' || matchMode === 'gte') {
                result.from = dateOnly;
                continue;
            }

            if (matchMode === 'dateBefore' || matchMode === 'before' || matchMode === 'lt' || matchMode === 'lte') {
                result.to = dateOnly;
                continue;
            }

            result.from = dateOnly;
            result.to = dateOnly;
        }

        return result;
    }

    private formatDateOnly(date: Date): string {
        const year = date.getFullYear();
        const month = `${date.getMonth() + 1}`.padStart(2, '0');
        const day = `${date.getDate()}`.padStart(2, '0');

        return `${year}-${month}-${day}`;
    }

    private saveFilters(filters: Record<string, FilterValue>): void {
        this.storage.set(PURCHASE_ORDER_ITEMS_LAST_FILTERS, filters);
    }

    private createTableFiltersFromStoredFilters(filters: Record<string, FilterValue>): Record<string, FilterMetadata> {
        const tableFilters: Record<string, FilterMetadata> = {};

        for (const [field, value] of Object.entries(filters)) {
            if (!this.isStoredFilterValueValid(value)) {
                continue;
            }

            const column = this.cols.find(col => col.field === field);

            tableFilters[field] = {
                value,
                matchMode: this.getStoredFilterMatchMode(column, value),
            };
        }

        return tableFilters;
    }

    private isStoredFilterValueValid(value: FilterValue): boolean {
        if (value === null || value === undefined || value === '') {
            return false;
        }

        if (Array.isArray(value)) {
            return value.length > 0;
        }

        if (this.isDateRangeFilterValue(value)) {
            return (value.from !== undefined && value.from !== '')
                || (value.to !== undefined && value.to !== '');
        }

        return true;
    }

    private getStoredFilterMatchMode(
        column: Column | undefined,
        value: FilterValue,
    ): string {
        if (Array.isArray(value)) {
            return 'in';
        }

        if (column?.type === 'date') {
            return 'dateIs';
        }

        if (column?.type === 'number' || column?.type === 'currency') {
            return 'equals';
        }

        return 'contains';
    }

    protected updatePurchaseOrderItemList(
        pageNumber: number,
        sortField: string | null = null,
        sortDirection: 'asc' | 'desc' | null = null,
        filters: Record<string, FilterValue> = {},
    ) {
        this.storage.set(PURCHASE_ORDER_ITEMS_LAST_PAGE_SIZE, this.itemsPerPage);

        this.purchaseOrderItemService
            .loadPurchaseOrderItems(
                pageNumber,
                this.itemsPerPage,
                sortField,
                sortDirection,
                filters,
            )
            .subscribe(purchaseOrderItemCollection => {
                this.purchaseOrderItems = purchaseOrderItemCollection.getPurchaseOrderItems();
                this.totalNbPurchaseOrderItems = purchaseOrderItemCollection.getTotalItems();
                this.loading = false;
            });
    }

    protected onColumnSelectChange(event: MultiSelectChangeEvent): void {
        const newSelection: Column[] = event.value ?? [];

        const stillSelected = this.selectedColumns.filter(existing =>
            newSelection.some(selected => selected.field === existing.field),
        );

        const added = newSelection.filter(selected =>
            !this.selectedColumns.some(existing => existing.field === selected.field),
        );

        this.selectedColumns = [...stillSelected, ...added];
        this.saveSelectedColumns();
    }

    protected onColReorder(event: TableColumnReorderEvent): void {
        if (event.dragIndex === undefined || event.dropIndex === undefined) {
            return;
        }

        const cols = [...this.selectedColumns];
        const [moved] = cols.splice(event.dragIndex, 1);
        cols.splice(event.dropIndex, 0, moved);

        this.selectedColumns = cols;
        this.saveSelectedColumns();
    }

    protected saveSelectedColumns(): void {
        this.storage.set(PURCHASE_ORDER_ITEMS_LAST_SELECTED_COLUMNS, this.selectedColumns);
    }

    protected getTextCellValue(
        purchaseOrderItem: PurchaseOrderItem,
        column: Column,
    ): string | number | Date | null {
        const rawValue = purchaseOrderItem[column.field];

        if (column.pipe === 'PurchaseOrderStatusPipe') {
            return this.purchaseOrderStatusPipe.transform(rawValue as any);
        }

        return rawValue ?? '';
    }

    protected getDateCellValue(
        purchaseOrderItem: PurchaseOrderItem,
        column: Column,
    ): Date | string | number | null {
        return purchaseOrderItem[column.field] ?? null;
    }

    protected getCurrencyCellValue(
        purchaseOrderItem: PurchaseOrderItem,
        column: Column,
    ): string | number | null {
        const value = purchaseOrderItem[column.field];

        if (value === null || value === undefined) {
            return null;
        }

        if (typeof value === 'string' || typeof value === 'number') {
            return value;
        }

        return null;
    }

    protected getDateRangeInputValue(
        value: unknown,
        bound: 'from' | 'to',
    ): string {
        if (!this.isDateRangeFilterValue(value)) {
            return '';
        }

        return value[bound] ?? '';
    }

    protected onDateRangeFromChange(
        from: string,
        currentValue: unknown,
        filterCallback: (value: DateRangeFilterValue | null) => void,
    ): void {
        const dateRange = this.normalizeDateRangeFilterValue(currentValue);
        dateRange.from = from || undefined;

        this.applyDateRangeFilter(dateRange, filterCallback);
    }

    protected onDateRangeToChange(
        to: string,
        currentValue: unknown,
        filterCallback: (value: DateRangeFilterValue | null) => void,
    ): void {
        const dateRange = this.normalizeDateRangeFilterValue(currentValue);
        dateRange.to = to || undefined;

        this.applyDateRangeFilter(dateRange, filterCallback);
    }

    private applyDateRangeFilter(
        dateRange: DateRangeFilterValue,
        filterCallback: (value: DateRangeFilterValue | null) => void,
    ): void {
        if (dateRange.from === undefined && dateRange.to === undefined) {
            filterCallback(null);
            return;
        }

        filterCallback(dateRange);
    }

    private normalizeDateRangeFilterValue(value: unknown): DateRangeFilterValue {
        if (!this.isDateRangeFilterValue(value)) {
            return {};
        }

        return {
            from: value.from,
            to: value.to,
        };
    }

    private isDateRangeFilterValue(value: unknown): value is DateRangeFilterValue {
        return typeof value === 'object'
            && value !== null
            && !(value instanceof Date)
            && !Array.isArray(value)
            && ('from' in value || 'to' in value);
    }
}
